/**
 * Data page: one tab per http/lookup section. Lookup card has name search (where applicable)
 * and id lookup fields (where the API has direct fetch-by-id). All fields sync: any lookup
 * (search or id) updates every other field from the returned object.
 */

import {
	createBadge,
	createButton,
	createCard,
	createCheckbox,
	createDropdown,
	createInput,
	createLookupFieldGroup,
	createMultiSelect,
	createSearchField,
	createTable,
	createTabset,
} from "@aiapollo/design-system"
import {
	buildProjectionJq,
	FILTER_OPERATOR_OPTIONS,
	FilterOperator,
	ActiveFilter,
} from "./dataUtils"
import { ICON_CELL, ICON_FORMULA, ICON_PLUS, ICON_PREVIEW, ICON_SORT_DOWN, ICON_SORT_UP, ICON_TABLE } from "../../icons"
import * as excelService from "../../services/excelService"
import * as lookup from "../../services/lookupService"
import { formatResourceFetchErrorMessage } from "../../utilities/errorMessages"
import { validateGenericId } from "../../utilities/validation"
import type { EntityDAO, InstrumentGroupDAO } from "../../services/types"

type RelatedColumn = { key: string; label: string }
type RelatedTabDef = { id: string; label: string }
type RelatedTabsConfig = {
	tabs: RelatedTabDef[]
	getContent: (
		obj: unknown,
		tabId: string,
	) => { columns: RelatedColumn[]; rows: Record<string, string | number | boolean>[] }
}

type IdFieldConfig = {
	key: string
	label: string
	fetchById: (id: string) => Promise<unknown>
	objectIdKey: string
}

const RESOURCE_SECTIONS: Array<{
	id: string
	label: string
	nameSearch?: boolean
	idFields?: IdFieldConfig[]
}> = [
	{
		id: "entities",
		label: "Entities",
		nameSearch: true,
		idFields: [
			{
				key: "entityId",
				label: "Entity ID",
				fetchById: lookup.fetchEntity,
				objectIdKey: "creditHqEntityIdentifier",
			},
		],
	},
	{
		id: "instrument-groups",
		label: "Instrument Groups",
		nameSearch: true,
		idFields: [
			{
				key: "instrumentGroupId",
				label: "Instrument Group ID",
				fetchById: lookup.fetchInstrumentGroup,
				objectIdKey: "creditHqInstrumentGroupIdentifier",
			},
		],
	},
	{
		id: "instruments",
		label: "Instruments",
		nameSearch: true,
		idFields: [
			{
				key: "instrumentId",
				label: "Instrument ID",
				fetchById: lookup.fetchInstrument,
				objectIdKey: "creditHqInstrumentIdentifier",
			},
		],
	},
	{
		id: "funds",
		label: "Funds",
		nameSearch: true,
		idFields: [
			{ key: "fundId", label: "Fund code", fetchById: lookup.fetchFund, objectIdKey: "fundIdentifier" },
		],
	},
	{
		id: "fund-trades",
		label: "Fund Trades",
		idFields: [
			{
				key: "fundTradeId",
				label: "Fund Trade code",
				fetchById: lookup.fetchFundTrade,
				objectIdKey: "creditHqFundTradeIdentifier",
			},
		],
	},
	{
		id: "fund-allocations",
		label: "Fund Allocations",
		idFields: [
			{
				key: "fundTradeId",
				label: "Fund Trade code",
				fetchById: lookup.fetchFundAllocations,
				objectIdKey: "fundTradeIdentifier",
			},
		],
	},
	{
		id: "fund-participants",
		label: "Fund Participants",
		nameSearch: true,
		idFields: [
			{
				key: "fundId",
				label: "Fund code",
				fetchById: lookup.fetchFundParticipants,
				objectIdKey: "fundIdentifier",
			},
		],
	},
	{
		id: "fund-pipelines",
		label: "Fund Pipelines",
		nameSearch: true,
		idFields: [
			{
				key: "pipelineId",
				label: "Pipeline ID",
				fetchById: lookup.fetchFundPipeline,
				objectIdKey: "pipelineId",
			},
		],
	},
	{
		id: "positions",
		label: "Positions",
		idFields: [
			{
				key: "fundId",
				label: "Fund code",
				fetchById: lookup.fetchPositions,
				objectIdKey: "fundId",
			},
			{
				key: "instrumentId",
				label: "Instrument code",
				fetchById: lookup.fetchPositionsByInstrument,
				objectIdKey: "creditHqInstrumentIdentifier",
			},
		],
	},
	{
		id: "rates",
		label: "Rates",
		nameSearch: true,
		idFields: [
			{
				key: "rateCode",
				label: "Rate code",
				fetchById: lookup.fetchRateObservations,
				objectIdKey: "rateCode",
			},
		],
	},
	{
		id: "trades",
		label: "Trades",
		idFields: [
			{
				key: "tradeId",
				label: "Trade code",
				fetchById: lookup.fetchTrade,
				objectIdKey: "creditHqTradeIdentifier",
			},
			{
				key: "instrumentId",
				label: "Instrument code",
				fetchById: lookup.fetchTradesByInstrument,
				objectIdKey: "creditHqInstrumentIdentifier",
			},
		],
	},
	{
		id: "quotes",
		label: "Quotes",
		idFields: [
			{
				key: "instrumentId",
				label: "Instrument code",
				fetchById: lookup.fetchQuotes,
				objectIdKey: "creditHqInstrumentIdentifier",
			},
		],
	},
	{
		id: "valuations",
		label: "Valuations",
		idFields: [
			{
				key: "valuationId",
				label: "Valuation code",
				fetchById: lookup.fetchValuation,
				objectIdKey: "valuationNumber",
			},
			{
				key: "instrumentId",
				label: "Instrument code",
				fetchById: lookup.fetchValuationsByInstrument,
				objectIdKey: "creditHqInstrumentIdentifier",
			},
		],
	},
	{ id: "sectors", label: "Sectors", nameSearch: true },
	{ id: "chart-of-accounts", label: "Chart of Accounts" },
]

const PRIMARY_TABS = RESOURCE_SECTIONS.map((s) => ({ id: s.id, label: s.label }))

const RELATED_TABS: Partial<Record<string, RelatedTabsConfig>> = {
	"instrument-groups": {
		tabs: [
			{ id: "participants", label: "Participants" },
			{ id: "useOfProceeds", label: "Use of Proceeds" },
			{ id: "covenants", label: "Covenants" },
			{ id: "instruments", label: "Instruments" },
		],
		getContent(obj, tabId) {
			const d = obj as InstrumentGroupDAO
			switch (tabId) {
				case "participants":
					return {
						columns: [
							{ key: "participantTypeName", label: "Participant Type" },
							{ key: "entityNameLongEnglish", label: "Entity Name" },
						],
						rows: lookup.extractParticipantsRows(d),
					}
				case "useOfProceeds":
					return {
						columns: [{ key: "useOfProceedsName", label: "Use of Proceeds" }],
						rows: lookup.extractUseOfProceedsRows(d),
					}
				case "covenants":
					return {
						columns: [
							{ key: "covenantCategoryName", label: "Category" },
							{ key: "covenantTypeName", label: "Type" },
							{ key: "isMaintenance", label: "Maintenance" },
							{ key: "isIncurrence", label: "Incurrence" },
						],
						rows: lookup.extractCovenantsRows(d),
					}
				case "instruments":
					return {
						columns: [
							{ key: "creditHqInstrumentIdentifier", label: "Instrument ID" },
						],
						rows: lookup.extractInstrumentsRows(d),
					}
				default:
					return { columns: [], rows: [] }
			}
		},
	},
	entities: {
		tabs: [
			{ id: "incorporation", label: "Incorporation" },
			{ id: "sectors", label: "Sectors" },
			{ id: "domicile", label: "Domicile" },
			{ id: "mappings", label: "Mappings" },
			{ id: "parentEntities", label: "Parent Entities" },
			{ id: "childEntities", label: "Child Entities" },
			{ id: "creditStatuses", label: "Credit Statuses" },
			{ id: "pendingActivities", label: "Pending Activities" },
		],
		getContent(obj, tabId) {
			const d = obj as EntityDAO
			switch (tabId) {
				case "incorporation":
					return {
						columns: [
							{ key: "incorporationCode", label: "Code" },
							{ key: "incorporationName", label: "Name" },
							{ key: "incorporationCountryNameShort", label: "Country" },
						],
						rows: lookup.extractIncorporationRows(d),
					}
				case "sectors":
					return {
						columns: [
							{ key: "marketName", label: "Market" },
							{ key: "sectorName", label: "Sector" },
							{ key: "subsectorName", label: "Subsector" },
							{ key: "industryName", label: "Industry" },
							{ key: "isPrimarySectorClassification", label: "Primary" },
						],
						rows: lookup.extractSectorsRows(d),
					}
				case "domicile":
					return {
						columns: [
							{ key: "firstLineStreetAddress", label: "Address" },
							{ key: "locality", label: "Locality" },
							{ key: "postalCode", label: "Postal Code" },
							{ key: "domicileCountryNameShort", label: "Country" },
						],
						rows: lookup.extractDomicileRows(d),
					}
				case "mappings":
					return {
						columns: [
							{ key: "externalProviderName", label: "Provider" },
							{ key: "externalProviderIdentifier", label: "Identifier" },
							{ key: "externalProviderProvidedName", label: "Provided Name" },
						],
						rows: lookup.extractMappingsRows(d),
					}
				case "parentEntities":
					return {
						columns: [
							{ key: "creditHqParentEntityIdentifier", label: "Parent ID" },
							{ key: "parentEntityNameLongEnglish", label: "Name" },
							{ key: "ownershipPercentage", label: "Ownership %" },
						],
						rows: lookup.extractParentEntitiesRows(d),
					}
				case "childEntities":
					return {
						columns: [
							{ key: "creditHqChildEntityIdentifier", label: "Child ID" },
							{ key: "childEntityNameLongEnglish", label: "Name" },
							{ key: "ownershipPercentage", label: "Ownership %" },
						],
						rows: lookup.extractChildEntitiesRows(d),
					}
				case "creditStatuses":
					return {
						columns: [
							{ key: "entityCreditStatusCode", label: "Code" },
							{ key: "entityCreditStatusName", label: "Status" },
						],
						rows: lookup.extractCreditStatusesRows(d),
					}
				case "pendingActivities":
					return {
						columns: [
							{ key: "entityPendingActionCode", label: "Code" },
							{ key: "entityPendingActionName", label: "Action" },
							{ key: "entityInMarket", label: "In Market" },
						],
						rows: lookup.extractPendingActivitiesRows(d),
					}
				default:
					return { columns: [], rows: [] }
			}
		},
	},
}

/** JQable lookups: for a looked-up object type, which related object types can be queried with JQ. */
const JQABLE_SECTIONS: Partial<Record<string, { id: string; label: string }[]>> = {
	"instrument-groups": [{ id: "funds", label: "Funds" }],
	funds: [
		{ id: "positions", label: "Positions" },
		{ id: "fund-participants", label: "Fund Participants" },
	],
	"fund-trades": [{ id: "fund-allocations", label: "Fund Allocations" }],
	instruments: [
		{ id: "positions", label: "Positions" },
		{ id: "quotes", label: "Quotes" },
	],
}

type JQableActionConfig = {
	/**
	 * Fetch rows for this section. When jqParam is provided it is forwarded as
	 * ?jq= to the API so filtering/sorting/projection happens server-side.
	 */
	fetchFn: (obj: unknown, jqParam?: string) => Promise<Record<string, unknown>[]>
	formulaName: string
	formulaArgs: (obj: unknown) => string[]
	/**
	 * When provided, formula can include a second (jq) arg from selected column keys,
	 * optional sort information (sort key + asc/desc), and active filters.
	 */
	formulaArgsWithColumns?: (
		obj: unknown,
		selectedKeys: string[],
		sortKey?: string,
		sortAsc?: boolean,
		filters?: ActiveFilter[],
	) => string[]
}

/** Known field keys per (resourceId, sectionId) for columns multiselect. Empty = discover from first row. */
const JQABLE_FIELD_KEYS: Partial<Record<string, Partial<Record<string, string[]>>>> = {
	"instrument-groups": {
		funds: [
			"fundIdentifier",
			"fundTickerInternal",
			"fundNameLongEnglish",
			"fundNameShortEnglish",
			"creditHqInstrumentGroupIdentifier",
		],
	},
	funds: {
		positions: [
			"fundId",
			"instrumentId",
			"quantity",
			"price",
			"asof",
			"priceAsof",
			"marketValuePositionCurrency",
			"marketValueReportingCurrency",
			"positionToReportingCurrencyFxRate",
			"marketValueCurrencyId",
			"marketValueReportingCurrencyId",
			"priceSourceId",
			"valuationId",
			"fundPositionId",
			"createdBy",
			"lastModifiedBy",
			"createTime",
			"lastModifiedTime",
		],
		"fund-participants": [
			"creditHqEntityIdentifier",
			"entityNameLongEnglish",
			"fundParticipantTypeName",
			"recordStatus",
		],
	},
	"fund-trades": {
		"fund-allocations": [
			"fundIdentifier",
			"fundTradeIdentifier",
			"fundAllocationAmount",
			"allocationCurrencyCode",
			"allocationTimestamp",
		],
	},
	instruments: {
		positions: [
			"fundId",
			"instrumentId",
			"quantity",
			"price",
			"asof",
			"priceAsof",
			"marketValuePositionCurrency",
			"marketValueReportingCurrency",
			"positionToReportingCurrencyFxRate",
			"marketValueCurrencyId",
			"marketValueReportingCurrencyId",
			"priceSourceId",
			"valuationId",
			"fundPositionId",
			"createdBy",
			"lastModifiedBy",
			"createTime",
			"lastModifiedTime",
		],
		quotes: ["asOf", "bidPrice", "midPrice", "askPrice", "bidSize", "askSize", "midSpread"],
	},
}

/** Per (resourceId, sectionId): fetch, formula name, and formula args for Reporting actions. */
const JQABLE_ACTIONS: Partial<Record<string, Partial<Record<string, JQableActionConfig>>>> = {
	"instrument-groups": {
	funds: {
		fetchFn: async (_obj, jqParam) => {
			const raw = await lookup.fetchFunds(jqParam)
			return (Array.isArray(raw) ? raw : []).map((x) => (x != null && typeof x === "object" ? (x as Record<string, unknown>) : {}))
		},
			formulaName: "getFunds",
			formulaArgs: () => [],
			formulaArgsWithColumns: (_obj, selectedKeys, sortKey, sortAsc, filters = []) => {
				const jq = buildProjectionJq(selectedKeys, sortKey, sortAsc, filters)
				return jq ? [jq] : []
			},
		},
	},
	funds: {
		positions: {
			fetchFn: async (obj, jqParam) => {
				const id = getObjKey(obj, "fundIdentifier")
				if (!id) return []
				const raw = await lookup.fetchPositions(id, jqParam)
				return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
			},
			formulaName: "getPositions",
			formulaArgs: (obj) => [getObjKey(obj, "fundIdentifier")],
			formulaArgsWithColumns: (obj, selectedKeys, sortKey, sortAsc, filters = []) => {
				const base = [getObjKey(obj, "fundIdentifier")]
				const jq = buildProjectionJq(selectedKeys, sortKey, sortAsc, filters)
				if (jq) base.push(jq)
				return base
			},
		},
		"fund-participants": {
			fetchFn: async (obj, jqParam) => {
				const id = getObjKey(obj, "fundIdentifier")
				if (!id) return []
				const raw = await lookup.fetchFundParticipants(id, jqParam)
				return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
			},
			formulaName: "getFundParticipants",
			formulaArgs: (obj) => [getObjKey(obj, "fundIdentifier")],
			formulaArgsWithColumns: (obj, selectedKeys, sortKey, sortAsc, filters = []) => {
				const base = [getObjKey(obj, "fundIdentifier")]
				const jq = buildProjectionJq(selectedKeys, sortKey, sortAsc, filters)
				if (jq) base.push(jq)
				return base
			},
		},
	},
	"fund-trades": {
		"fund-allocations": {
			fetchFn: async (obj, jqParam) => {
				const id = getObjKey(obj, "creditHqFundTradeIdentifier")
				if (!id) return []
				const raw = await lookup.fetchFundAllocations(id, jqParam)
				return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
			},
			formulaName: "getFundAllocationAttributeByFundTradeIdentifier",
			formulaArgs: (obj) => [getObjKey(obj, "creditHqFundTradeIdentifier")],
			formulaArgsWithColumns: (obj, selectedKeys, sortKey, sortAsc, filters = []) => {
				const base = [getObjKey(obj, "creditHqFundTradeIdentifier")]
				const jq = buildProjectionJq(selectedKeys, sortKey, sortAsc, filters)
				if (jq) base.push(jq)
				return base
			},
		},
	},
	instruments: {
		positions: {
			fetchFn: async (obj, jqParam) => {
				const id = getObjKey(obj, "creditHqInstrumentIdentifier")
				if (!id) return []
				const raw = await lookup.fetchPositionsByInstrument(id, jqParam)
				return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
			},
			formulaName: "getPositionsByInstrument",
			formulaArgs: (obj) => [getObjKey(obj, "creditHqInstrumentIdentifier")],
			formulaArgsWithColumns: (obj, selectedKeys, sortKey, sortAsc, filters = []) => {
				const base = [getObjKey(obj, "creditHqInstrumentIdentifier")]
				const jq = buildProjectionJq(selectedKeys, sortKey, sortAsc, filters)
				if (jq) base.push(jq)
				return base
			},
		},
		quotes: {
			fetchFn: async (obj, jqParam) => {
				const id = getObjKey(obj, "creditHqInstrumentIdentifier")
				if (!id) return []
				const raw = await lookup.fetchQuotes(id, jqParam)
				return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
			},
			formulaName: "getQuotes",
			formulaArgs: (obj) => [getObjKey(obj, "creditHqInstrumentIdentifier")],
			formulaArgsWithColumns: (obj, selectedKeys, sortKey, sortAsc, filters = []) => {
				const base = [getObjKey(obj, "creditHqInstrumentIdentifier")]
				const jq = buildProjectionJq(selectedKeys, sortKey, sortAsc, filters)
				if (jq) base.push(jq)
				return base
			},
		},
	},
}

/** Convert array of objects to 2D array [header, ...rows] for Excel range.values. Keys optional: when provided, only those columns. */
function arrayToTableRows(
	arr: Record<string, unknown>[],
	keys?: string[],
): (string | number)[][] {
	if (arr.length === 0) return [["(no rows)", ""]]
	const useKeys = keys?.length ? keys : Object.keys(arr[0] ?? {})
	const header = useKeys.map((k) => toCapitalizedLabel(k))
	const rows = arr.map((item) =>
		useKeys.map((k) => {
			const v = item[k]
			if (v == null) return ""
			if (typeof v === "number") return v
			if (typeof v === "object") return JSON.stringify(v)
			return String(v)
		}),
	)
	return [header, ...rows]
}

/**
 * Build JQ that applies optional sort, filters, and projection from the API result array.
 * - sort first (operates on the whole array), then iterate, then filter, then project.
 * - When nothing supplied: returns empty string (no jq arg needed).
 */

const LOOKUP_DEBOUNCE_MS = 400
const LOOKUP_MIN_CHARS = 2
const LOOKUP_MAX_RESULTS = 20

type SearchByNameFn = (name: string) => Promise<unknown[]>

/**
 * Maps field keys that appear in detail objects to the resource IDs they reference.
 * When a detail value is rendered and its key appears here, it becomes a clickable
 * link that navigates to the target resource and triggers a lookup.
 *
 * Both "internal" keys (e.g. fundId) and API identifier keys (e.g. fundIdentifier)
 * are included so coverage is maximised regardless of which the API returns.
 */
const LOOKUPABLE_FIELD_KEYS: Readonly<Record<string, string>> = {
	// ── Fund references ────────────────────────────────────────────────────────
	fundId: "funds",
	fundIdentifier: "funds",
	// ── Instrument references ──────────────────────────────────────────────────
	instrumentId: "instruments",
	creditHqInstrumentIdentifier: "instruments",
	// ── Instrument group references ────────────────────────────────────────────
	creditHqInstrumentGroupIdentifier: "instrument-groups",
	// ── Entity references (self, parent, child) ────────────────────────────────
	entityId: "entities",
	creditHqEntityIdentifier: "entities",
	// Parent/child entity IDs appear in the Entities Related → Parent/Child Entities tabs
	creditHqParentEntityIdentifier: "entities",
	creditHqChildEntityIdentifier: "entities",
	// ── Fund trade references ──────────────────────────────────────────────────
	fundTradeId: "fund-trades",
	creditHqFundTradeIdentifier: "fund-trades",
	// ── Trade references ───────────────────────────────────────────────────────
	tradeId: "trades",
	creditHqTradeIdentifier: "trades",
	// ── Other ─────────────────────────────────────────────────────────────────
	valuationId: "valuations",
	valuationNumber: "valuations",
	pipelineId: "fund-pipelines",
	rateCode: "rates",
}

/** Callback signature for cross-resource navigation + lookup. */
type NavigateAndLookupFn = (resourceId: string, id: string) => void

const SEARCH_BY_NAME: Partial<Record<string, SearchByNameFn>> = {
	entities: lookup.searchEntitiesByName,
	"instrument-groups": lookup.searchInstrumentGroups,
	instruments: lookup.searchInstruments,
	funds: lookup.searchFundsByName,
	"fund-participants": lookup.searchFundParticipants,
	"fund-pipelines": lookup.searchFundPipelines,
	rates: lookup.searchRates,
	sectors: lookup.searchSectors,
}

function itemDisplayLabel(item: unknown): string {
	if (item == null || typeof item !== "object") return String(item)
	const o = item as Record<string, unknown>
	const keys = [
		"entityDisplayName",
		"entity_name_long_english",
		"entityNameLongEnglish",
		"instrumentGroupName",
		"instrument_group_name",
		"fundNameLongEnglish",
		"fund_name_long_english",
		"name",
		"label",
		"marketName",
		"sectorName",
	]
	for (const k of keys) {
		const v = o[k]
		if (v != null && String(v).trim() !== "") return String(v).trim()
	}
	for (const v of Object.values(o)) {
		if (v != null && typeof v === "string" && v.trim() !== "") return v.trim()
	}
	return "(no label)"
}

function getObjKey(obj: unknown, key: string): string {
	if (obj == null || typeof obj !== "object") return ""
	const o = obj as Record<string, unknown>
	const snake = key.replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "")
	const v = o[key] ?? o[snake]
	return v != null ? String(v).trim() : ""
}

/** Convert camelCase or snake_case to capitalized label (e.g. "Entity Name Long English"). */
function toCapitalizedLabel(key: string): string {
	const withSpaces = key
		.replace(/([A-Z])/g, " $1")
		.replace(/_/g, " ")
		.trim()
	if (!withSpaces) return key
	return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase()
}

/** Keys that duplicate lookup/search fields (id or name); exclude from primary info. */
const REDUNDANT_DETAIL_KEYS = new Set([
	"creditHqEntityIdentifier",
	"credit_hq_entity_identifier",
	"entityDisplayName",
	"entity_display_name",
	"entityNameLongEnglish",
	"entity_name_long_english",
	"creditHqInstrumentGroupIdentifier",
	"credit_hq_instrument_group_identifier",
	"instrumentGroupName",
	"instrument_group_name",
	"creditHqInstrumentIdentifier",
	"credit_hq_instrument_identifier",
	"fundIdentifier",
	"fund_identifier",
	"creditHqFundTradeIdentifier",
	"credit_hq_fund_trade_identifier",
	"creditHqTradeIdentifier",
	"credit_hq_trade_identifier",
	"valuationNumber",
	"valuation_number",
	"pipelineId",
	"pipeline_id",
	"rateCode",
	"rate_code",
	"fundNameLongEnglish",
	"fund_name_long_english",
	"fundNameShortEnglish",
	"fund_name_short_english",
])

function isRedundantDetailKey(key: string): boolean {
	if (REDUNDANT_DETAIL_KEYS.has(key)) return true
	const lower = key.toLowerCase()
	if (lower.endsWith("identifier") || lower.endsWith("id") || lower === "id") return true
	return false
}

function hasDisplayableLookupObject(obj: unknown): boolean {
	if (obj == null || typeof obj !== "object") return false
	const o = obj as Record<string, unknown>
	return Object.entries(o).some(
		([k, v]) =>
			!isRedundantDetailKey(k) &&
			v != null &&
			typeof v !== "object" &&
			!Array.isArray(v),
	)
}

// ─── Filters (types/functions imported from dataUtils.ts) ────────────────────

// ─────────────────────────────────────────────────────────────────────────────

function renderRelatedTable(
	container: HTMLElement,
	columns: RelatedColumn[],
	rows: Record<string, string | number | boolean>[],
	emptyMessage: string,
	navigateAndLookup?: NavigateAndLookupFn,
): void {
	container.innerHTML = ""
	const headers = columns.map((c) => c.label)
	const stringRows = rows.map((row) =>
		columns.map((col) => {
			const v = row[col.key]
			if (v === true) return "Yes"
			if (v === false) return "No"
			return v != null ? String(v) : ""
		}),
	)
	if (stringRows.length === 0) {
		const p = document.createElement("p")
		p.className = "lookup-card__related-empty"
		p.textContent = emptyMessage
		container.appendChild(p)
		return
	}
	const tableRoot = createTable({
		headers,
		rows: stringRows,
		variant: "bordered",
	})

	// Convert cells for lookupable columns to clickable navigation buttons.
	if (navigateAndLookup) {
		const tbody = tableRoot.querySelector("tbody")
		const trEls = tbody ? Array.from(tbody.querySelectorAll("tr")) : []
		trEls.forEach((tr, rowIdx) => {
			const tds = Array.from(tr.querySelectorAll("td"))
			columns.forEach((col, colIdx) => {
				const targetResourceId = LOOKUPABLE_FIELD_KEYS[col.key]
				if (!targetResourceId) return
				const td = tds[colIdx]
				const cellText = stringRows[rowIdx]?.[colIdx] ?? ""
				if (td && cellText) {
					td.innerHTML = ""
					const btn = document.createElement("button")
					btn.type = "button"
					btn.className = "lookup-card__info-link"
					btn.textContent = cellText
					const label = RESOURCE_SECTIONS.find((s) => s.id === targetResourceId)?.label ?? targetResourceId
					btn.setAttribute("title", `Look up in ${label}`)
					btn.addEventListener("click", () => navigateAndLookup(targetResourceId, cellText))
					td.appendChild(btn)
				}
			})
		})
	}

	container.appendChild(tableRoot)
}

/**
 * Render primary object info: label-value groups, flex wrap, capitalized labels.
 * Omits id/name keys redundant with lookup fields.
 * If navigateAndLookup is provided, string values for keys in LOOKUPABLE_FIELD_KEYS
 * are rendered as clickable links that navigate to the target resource.
 */
function renderLookupPrimaryInfo(
	container: HTMLElement,
	booleanContainer: HTMLElement,
	obj: unknown,
	navigateAndLookup?: NavigateAndLookupFn,
): void {
	container.innerHTML = ""
	booleanContainer.innerHTML = ""
	if (obj == null || typeof obj !== "object") return
	const o = obj as Record<string, unknown>
	const allEntries = Object.entries(o).filter(
		([k, v]) =>
			!isRedundantDetailKey(k) &&
			v != null &&
			typeof v !== "object" &&
			!Array.isArray(v),
	) as [string, string | number | boolean][]
	if (allEntries.length === 0) return

	const boolEntries = allEntries.filter(([, v]) => typeof v === "boolean")
	const nonBoolEntries = allEntries.filter(([, v]) => typeof v !== "boolean")

	// Non-boolean fields: rendered as labeled info items in a flex-wrap grid.
	nonBoolEntries.slice(0, 30).forEach(([k, v]) => {
		const item = document.createElement("div")
		item.className = "lookup-card__info-item"
		const label = document.createElement("span")
		label.className = "lookup-card__info-label"
		label.textContent = toCapitalizedLabel(k)
		item.appendChild(label)

		const targetResourceId =
			navigateAndLookup && typeof v === "string" && v.length > 0
				? LOOKUPABLE_FIELD_KEYS[k]
				: undefined

		if (targetResourceId) {
			const btn = document.createElement("button")
			btn.type = "button"
			btn.className = "lookup-card__info-link"
			btn.textContent = String(v)
			btn.setAttribute("title", `Look up in ${RESOURCE_SECTIONS.find((s) => s.id === targetResourceId)?.label ?? targetResourceId}`)
			btn.addEventListener("click", () => navigateAndLookup!(targetResourceId, String(v)))
			item.appendChild(btn)
		} else {
			const value = document.createElement("span")
			value.className = "lookup-card__info-value"
			value.textContent = String(v)
			item.appendChild(value)
		}

		container.appendChild(item)
	})

	// Boolean fields: rendered as read-only labeled checkboxes below the info grid.
	boolEntries.forEach(([k, v]) => {
		const cb = createCheckbox({ label: toCapitalizedLabel(k), checked: v as boolean, readonly: true })
		booleanContainer.appendChild(cb.root)
	})
}

function buildLookupCard(
	resourceId: string,
	reportingCard: HTMLElement,
	navigateAndLookup: NavigateAndLookupFn,
	lookupRegistry: Map<string, (id: string) => void>,
): HTMLElement {
	const section = RESOURCE_SECTIONS.find((s) => s.id === resourceId)
	const wrapper = document.createElement("div")
	wrapper.className = "lookup-cards-wrap"

	if (!section || (!section.nameSearch && !section.idFields?.length)) {
		const card = createCard({ title: "Lookup", padding: "md" })
		const content = document.createElement("div")
		content.className = "card__content"
		const p = document.createElement("p")
		p.textContent =
			section?.id === "chart-of-accounts"
				? "List only."
				: "No search or lookup for this section."
		content.appendChild(p)
		card.appendChild(content)
		wrapper.appendChild(card)
		return wrapper
	}

	const pairs: { setValue: (v: string) => void; getValue: (obj: unknown) => string }[] = []
	const fillFromObjectRef = { current: (_obj: unknown) => {} }
	const idFields = section.idFields ?? []
	const sortedIdFields = [...idFields].sort((a, b) =>
		a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
	)
	const primaryIdField = sortedIdFields[0]
	const primaryIdObjectKey = primaryIdField?.objectIdKey
	const resolvePrimaryLookupId = (item: unknown): string =>
		primaryIdObjectKey ? getObjKey(item, primaryIdObjectKey) : ""
	let firstIdGroupRunLookup: ((id?: string) => Promise<void>) | null = null

	// Card 1: Lookup fields
	const fieldsCard = createCard({ title: "Lookup", padding: "md" })
	fieldsCard.classList.add("lookup-fields-card")
	const fieldsContent = document.createElement("div")
	fieldsContent.className = "card__content lookup-card__fields"
	if (section.nameSearch) {
		const searchFn = SEARCH_BY_NAME[resourceId]
		const searchField = createSearchField({
			label: "Name",
			placeholder: "Type to search…",
			minChars: LOOKUP_MIN_CHARS,
			debounceMs: LOOKUP_DEBOUNCE_MS,
			maxResults: LOOKUP_MAX_RESULTS,
			fetchResults: searchFn ? (q) => searchFn(q) : () => Promise.resolve([]),
			getItemLabel: itemDisplayLabel,
			onSelect: (item) => {
				const selectedId = resolvePrimaryLookupId(item)
				if (selectedId && firstIdGroupRunLookup) {
					// Search rows are often summary objects; re-hydrate through ID lookup so
					// Details/Related render from the same full payload path as manual ID entry.
					void firstIdGroupRunLookup(selectedId).catch(() => {
						fillFromObjectRef.current(item)
					})
					return
				}
				fillFromObjectRef.current(item)
			},
		})
		pairs.push({
			setValue: (v) => {
				searchField.input.value = v
			},
			getValue: itemDisplayLabel,
		})
		fieldsContent.appendChild(searchField.root)
	}
	for (const idField of sortedIdFields) {
		const idGroup = createLookupFieldGroup({
			label: idField.label,
			placeholder: "ID or code",
			resourceLabel: section.label,
			fetchById: idField.fetchById,
			onResult: (data) => fillFromObjectRef.current(data),
			validate: (id) => validateGenericId(id, section.label),
			trailingIcon: ICON_CELL,
			trailingIconAriaLabel: "Use selected cell",
			onTrailingIconClick: (runLookup) => {
				excelService.readSelectedCellValue().then((id) => {
					if (id) runLookup(id)
				})
			},
		})
		// Register the first (primary) id field's runLookup so connective
		// navigation can trigger a lookup on this resource from elsewhere.
		if (firstIdGroupRunLookup === null) {
			firstIdGroupRunLookup = idGroup.runLookup
			lookupRegistry.set(resourceId, (id: string) => void idGroup.runLookup(id))
		}
		pairs.push({
			setValue: idGroup.setValue,
			getValue: (obj) => getObjKey(obj, idField.objectIdKey),
		})
		fieldsContent.appendChild(idGroup.root)
	}
	fieldsCard.appendChild(fieldsContent)
	wrapper.appendChild(fieldsCard)

	// Card 2: Main details (primary info + boolean checkboxes)
	const mainDetailsCard = createCard({ title: "Details", padding: "md", elevation: "sm" })
	mainDetailsCard.classList.add("lookup-details-card", "lookup-details-card--empty")
	const mainDetailsContent = document.createElement("div")
	mainDetailsContent.className = "card__content lookup-card__primary-info"
	mainDetailsContent.setAttribute("aria-live", "polite")
	mainDetailsCard.appendChild(mainDetailsContent)
	// Boolean values rendered as read-only checkboxes, separated below the info items grid.
	const mainDetailsBooleansContent = document.createElement("div")
	mainDetailsBooleansContent.className = "lookup-card__booleans"
	mainDetailsCard.appendChild(mainDetailsBooleansContent)
	wrapper.appendChild(mainDetailsCard)

	// Card 3: Related (tabset in header, table in content)
	const relatedConfig = RELATED_TABS[resourceId]
	const relatedPanelRefs = new Map<string, HTMLElement>()
	const relatedTabsetRef: {
		current: { updateTabCount: (id: string, n: number) => void } | null
	} = { current: null }
	let relatedCard: HTMLElement | null = null
	if (relatedConfig) {
		relatedCard = createCard({ title: "Related", padding: "md", elevation: "sm" })
		relatedCard.classList.add("lookup-related-card", "lookup-related-card--empty")
		const tabset = createTabset({
			tabs: relatedConfig.tabs,
			activeTabId: relatedConfig.tabs[0]?.id ?? "",
			type: "secondary",
			getPanel: (tabId) => {
				const panel = document.createElement("div")
				panel.className = "lookup-card__related-panel"
				relatedPanelRefs.set(tabId, panel)
				return panel
			},
			ariaLabel: "Related records",
			overflowDropdown: true,
			overflowDropdownFullWidth: true,
		})
		relatedTabsetRef.current = tabset
		const tabsetContent = tabset.root.querySelector(".ds-tabset__content")
		// Keep tabset root in DOM (in card header) so --secondary and overflow dropdown styles/behavior apply
		const relatedHeader = document.createElement("div")
		relatedHeader.className = "card__header"
		relatedHeader.appendChild(tabset.root)
		const relatedContent = document.createElement("div")
		relatedContent.className = "card__content"
		if (tabsetContent) relatedContent.appendChild(tabsetContent)
		relatedCard.appendChild(relatedHeader)
		relatedCard.appendChild(relatedContent)
		wrapper.appendChild(relatedCard)
	}

	function fillFromObject(obj: unknown): void {
		for (const { setValue, getValue } of pairs) {
			setValue(getValue(obj))
		}
		renderLookupPrimaryInfo(mainDetailsContent, mainDetailsBooleansContent, obj, navigateAndLookup)
		const hasDisplayable = hasDisplayableLookupObject(obj)
		mainDetailsCard.classList.toggle("lookup-details-card--empty", !hasDisplayable)
		const hasObj = obj != null && typeof obj === "object"
		const jqableSections = JQABLE_SECTIONS[resourceId]
		const hasJqable = hasObj && jqableSections != null && jqableSections.length > 0
		reportingCard.classList.toggle("reporting-sections--empty", !hasJqable)
		reportingCard.innerHTML = ""
		if (hasJqable && jqableSections.length > 0) {
			const actionsBySection = JQABLE_ACTIONS[resourceId]
			const fieldKeysBySection = JQABLE_FIELD_KEYS[resourceId]
			for (const section of jqableSections) {
				const action = actionsBySection?.[section.id]
				if (!action) continue
				{
				const sectionCard = createCard({ title: section.label, elevation: "sm", padding: "md" })
				const sectionRow = document.createElement("div")
				sectionRow.className = "card__content reporting-card__section"
				const predefinedFieldKeys = fieldKeysBySection?.[section.id] ?? []
				// liveFieldOptions is updated after the first successful fetch (dynamic discovery).
				let liveFieldOptions = predefinedFieldKeys.map((k) => ({
					value: k,
					label: toCapitalizedLabel(k),
				}))
				// Alias for initial dropdown creation below.
				const columnsOptions = liveFieldOptions
					const columnsMultiselect = createMultiSelect({
						id: `reporting-${resourceId}-${section.id}-columns`,
						label: "Columns",
						options: columnsOptions,
						placeholder: "All",
						listboxAriaLabel: `Select columns for ${section.label}`,
					})
					let sortAsc = true
					const sortByDropdown = createDropdown({
						id: `reporting-${resourceId}-${section.id}-sort`,
						label: "Sort by",
						options: columnsOptions,
						placeholder: "—",
						initialSelected: predefinedFieldKeys[0] ?? "",
						listboxAriaLabel: `Sort ${section.label} by`,
						rightToggleIcon: {
							iconUp: ICON_SORT_UP,
							iconDown: ICON_SORT_DOWN,
							initialAsc: true,
							ariaLabelAsc: "Sort ascending",
							ariaLabelDesc: "Sort descending",
							onToggle: (asc) => {
								sortAsc = asc
							},
						},
					})
				const selectorRow = document.createElement("div")
				selectorRow.className = "reporting-card__selector-row"
				if (columnsOptions.length > 0) {
					selectorRow.appendChild(columnsMultiselect.root)
					selectorRow.appendChild(sortByDropdown.root)
				}

				// ── Filters section ──────────────────────────────────────────
				const activeFilters: ActiveFilter[] = []

				const filtersSection = document.createElement("div")
				filtersSection.className = "reporting-card__filters"

				const filtersLabel = document.createElement("span")
				filtersLabel.className = "reporting-card__filters-label"
				filtersLabel.textContent = "Filters"
				filtersSection.appendChild(filtersLabel)

				const filterRow = document.createElement("div")
				filterRow.className = "reporting-card__filter-row"

				const filterFields = document.createElement("div")
				filterFields.className = "reporting-card__filter-fields"

				const fieldDropdown = createDropdown({
					id: `filter-${resourceId}-${section.id}-field`,
					label: "Field",
					options: columnsOptions,
					placeholder: "Field",
					initialSelected: columnsOptions[0]?.value ?? "",
					listboxAriaLabel: "Filter by field",
				})
				const operatorDropdown = createDropdown({
					id: `filter-${resourceId}-${section.id}-operator`,
					label: "Operator",
					options: FILTER_OPERATOR_OPTIONS,
					placeholder: "Op",
					initialSelected: "eq",
					listboxAriaLabel: "Filter operator",
				})
				const valueWrap = createInput({ label: "Value", placeholder: "Value…" })
				const valueInput = valueWrap.querySelector("input") as HTMLInputElement

				filterFields.appendChild(fieldDropdown.root)
				filterFields.appendChild(operatorDropdown.root)
				filterFields.appendChild(valueWrap)

				const addFilterBtn = createButton({
					variant: "outline",
					size: "sm",
					icon: ICON_PLUS,
					ariaLabel: "Add filter",
				})

				filterRow.appendChild(filterFields)
				filterRow.appendChild(addFilterBtn)
				filtersSection.appendChild(filterRow)

				/**
				 * Called after any successful data fetch. Merges predefined field keys
				 * with all keys discovered in the actual rows, then refreshes all three
				 * option-bearing dropdowns so users can access every available column.
				 */
				function updateFieldOptions(rows: Record<string, unknown>[]): void {
					if (rows.length === 0) return
					const discovered = new Set<string>()
					for (const row of rows) {
						for (const key of Object.keys(row)) discovered.add(key)
					}
					// Predefined keys first (maintains preferred ordering), then alphabetical extras
					const merged = [...predefinedFieldKeys]
					;[...discovered]
						.filter((k) => !predefinedFieldKeys.includes(k))
						.sort()
						.forEach((k) => merged.push(k))
					liveFieldOptions = merged.map((k) => ({ value: k, label: toCapitalizedLabel(k) }))
					columnsMultiselect.setOptions(liveFieldOptions)
					sortByDropdown.setOptions(liveFieldOptions)
					fieldDropdown.setOptions(liveFieldOptions)
				}

				const badgesContainer = document.createElement("div")
				badgesContainer.className = "reporting-card__filter-badges"
				filtersSection.appendChild(badgesContainer)

				function renderBadges() {
					badgesContainer.innerHTML = ""
					activeFilters.forEach((filter, idx) => {
						const opLabel = FILTER_OPERATOR_OPTIONS.find((o) => o.value === filter.operator)?.label ?? filter.operator
						const fieldLabel = liveFieldOptions.find((o) => o.value === filter.field)?.label ?? filter.field
						const badge = createBadge({
							label: `${fieldLabel} ${opLabel} ${filter.value}`,
							onRemove: () => {
								activeFilters.splice(idx, 1)
								renderBadges()
							},
							removeAriaLabel: `Remove filter: ${fieldLabel} ${opLabel} ${filter.value}`,
						})
						badgesContainer.appendChild(badge.root)
					})
				}

				addFilterBtn.addEventListener("click", () => {
					const field = fieldDropdown.getSelected()[0]
					const operator = operatorDropdown.getSelected()[0] as FilterOperator
					const value = valueInput.value.trim()
					if (!field || !operator || !value) return
					activeFilters.push({ field, operator, value })
					valueInput.value = ""
					renderBadges()
				})

				valueInput.addEventListener("keydown", (e) => {
					if (e.key === "Enter") addFilterBtn.click()
				})
				// ────────────────────────────────────────────────────────────

				const actionsEl = document.createElement("div")
				actionsEl.className = "reporting-card__actions"
				const previewBtn = createButton({
					variant: "outline",
					size: "sm",
					icon: ICON_PREVIEW,
					ariaLabel: `Preview ${section.label} in table`,
				})
				const insertTableBtn = createButton({
					variant: "primary",
					size: "sm",
					icon: ICON_TABLE,
					ariaLabel: `Insert ${section.label} into worksheet`,
				})
				const insertFormulaBtn = createButton({
					variant: "primary",
					size: "sm",
					icon: ICON_FORMULA,
					ariaLabel: `Insert ${section.label} lookup formula`,
				})
				const previewContainer = document.createElement("div")
				previewContainer.className = "reporting-card__preview reporting-card__preview--empty"
				previewContainer.setAttribute("aria-live", "polite")
				const statusEl = document.createElement("div")
				statusEl.className = "reporting-card__status"
				statusEl.setAttribute("role", "status")
				statusEl.setAttribute("aria-live", "polite")

			previewBtn.addEventListener("click", async () => {
				previewContainer.innerHTML = ""
				previewContainer.classList.add("reporting-card__preview--empty")
				statusEl.textContent = ""
				previewBtn.disabled = true
				try {
					const selectedKeys = columnsMultiselect.getSelected()
					const sortKey = sortByDropdown.getSelected()[0]
					const jqParam = buildProjectionJq(selectedKeys, sortKey, sortAsc, activeFilters) || undefined
					const rows = await action.fetchFn(obj, jqParam)
					// Field options are discovered from a raw (unprojected) fetch the first time,
					// but update from whatever the API returns so new keys surface immediately.
					updateFieldOptions(rows)
					if (rows.length === 0) {
						previewContainer.textContent = "No data."
						previewContainer.classList.remove("reporting-card__preview--empty")
						return
					}
					const useKeys = selectedKeys.length > 0 ? selectedKeys : Object.keys(rows[0] ?? {})
					const columns: RelatedColumn[] = useKeys.map((k) => ({
						key: k,
						label: toCapitalizedLabel(k),
					}))
					const typedRows = rows.map((r) => {
						const out: Record<string, string | number | boolean> = {}
						for (const col of columns) {
							const v = r[col.key]
							if (v === true || v === false) out[col.key] = v
							else if (typeof v === "number") out[col.key] = v
							else out[col.key] = String(v ?? "")
						}
						return out
					})
					previewContainer.classList.remove("reporting-card__preview--empty")
					renderRelatedTable(previewContainer, columns, typedRows, "No data.")
				} catch (err) {
					previewContainer.textContent = formatResourceFetchErrorMessage(err, section.label, "")
					previewContainer.classList.remove("reporting-card__preview--empty")
				} finally {
					previewBtn.disabled = false
				}
			})

			insertTableBtn.addEventListener("click", async () => {
				statusEl.textContent = ""
				insertTableBtn.disabled = true
				try {
					const selectedKeys = columnsMultiselect.getSelected()
					const sortKey = sortByDropdown.getSelected()[0]
					const jqParam = buildProjectionJq(selectedKeys, sortKey, sortAsc, activeFilters) || undefined
					const rows = await action.fetchFn(obj, jqParam)
					updateFieldOptions(rows)
					const useKeys = selectedKeys.length > 0 ? selectedKeys : undefined
					const tableValues = arrayToTableRows(rows, useKeys)
					const result = await excelService.writeValuesToSelection(tableValues)
					if (result.success) {
						statusEl.textContent = "Table inserted."
					} else {
						statusEl.textContent = result.message
					}
				} catch (err) {
					statusEl.textContent = formatResourceFetchErrorMessage(err, section.label, "")
				} finally {
					insertTableBtn.disabled = false
				}
			})

				insertFormulaBtn.addEventListener("click", async () => {
					statusEl.textContent = ""
					insertFormulaBtn.disabled = true
					try {
						const selectedKeys = columnsMultiselect.getSelected()
						const args = action.formulaArgsWithColumns
							? action.formulaArgsWithColumns(
									obj,
									selectedKeys,
									sortByDropdown.getSelected()[0],
									sortAsc,
									activeFilters,
								)
							: action.formulaArgs(obj)
						const formula = `=AIA.${action.formulaName}(${args.map((a) => `"${String(a).replace(/"/g, '""')}"`).join(", ")})`
						const result = await excelService.writeFormulaToSelection(formula)
						if (result.success) {
							statusEl.textContent = "Formula inserted."
						} else {
							statusEl.textContent = result.message
						}
					} catch (err) {
						statusEl.textContent = formatResourceFetchErrorMessage(err, section.label, "")
					} finally {
						insertFormulaBtn.disabled = false
					}
				})

			actionsEl.appendChild(previewBtn)
			actionsEl.appendChild(insertTableBtn)
			actionsEl.appendChild(insertFormulaBtn)
			sectionRow.appendChild(selectorRow)
			sectionRow.appendChild(filtersSection)
			sectionRow.appendChild(actionsEl)
			sectionRow.appendChild(statusEl)
			sectionRow.appendChild(previewContainer)
			sectionCard.appendChild(sectionRow)
			reportingCard.appendChild(sectionCard)
				}
			}
		}
		if (relatedCard) {
			relatedCard.classList.toggle("lookup-related-card--empty", !hasObj)
			if (hasObj && relatedConfig && relatedTabsetRef.current) {
				for (const tab of relatedConfig.tabs) {
					const { columns, rows } = relatedConfig.getContent(obj, tab.id)
					const panel = relatedPanelRefs.get(tab.id)
					if (panel)
						renderRelatedTable(panel, columns, rows, "No data.", navigateAndLookup)
					relatedTabsetRef.current.updateTabCount(tab.id, rows.length)
				}
			}
		}
	}
	fillFromObjectRef.current = fillFromObject

	return wrapper
}

function buildReportingCard(_resourceId: string): HTMLElement {
	const wrap = document.createElement("div")
	wrap.className = "reporting-sections reporting-sections--empty"
	wrap.setAttribute("aria-live", "polite")
	return wrap
}

function buildTabPanel(
	resourceId: string,
	navigateAndLookup: NavigateAndLookupFn,
	lookupRegistry: Map<string, (id: string) => void>,
): HTMLElement {
	const panel = document.createElement("div")
	panel.className = "lookup-section"
	const reportingCard = buildReportingCard(resourceId)
	panel.appendChild(buildLookupCard(resourceId, reportingCard, navigateAndLookup, lookupRegistry))
	panel.appendChild(reportingCard)
	return panel
}

export function createDataPage(): HTMLElement {
	const root = document.createElement("div")
	root.setAttribute("role", "main")
	root.className = "data-page"

	// Registry of per-resource lookup triggers, populated lazily as each tab panel is built.
	const lookupRegistry = new Map<string, (id: string) => void>()

	// Forward ref avoids chicken-and-egg: navigateAndLookup needs primaryTabset.showTab,
	// but primaryTabset needs navigateAndLookup in its getPanel callback.
	const tabsetRef: { current: ReturnType<typeof createTabset> | null } = { current: null }

	function scrollToTop(): void {
		root.scrollTo({ top: 0, behavior: "smooth" })
	}

	function navigateAndLookup(resourceId: string, id: string): void {
		// showTab builds the panel lazily if needed, which registers the lookup function.
		// onTabChange will also call scrollToTop, so we only need it here when the tab
		// doesn't change (same resource navigated to from a different field on same tab).
		tabsetRef.current?.showTab(resourceId)
		lookupRegistry.get(resourceId)?.(id)
	}

	const primaryTabset = createTabset({
		tabs: PRIMARY_TABS,
		activeTabId: PRIMARY_TABS[0]?.id ?? "",
		type: "primary",
		getPanel: (tabId) => buildTabPanel(tabId, navigateAndLookup, lookupRegistry),
		ariaLabel: "Data sections",
		overflowDropdown: true,
		overflowDropdownFullWidth: true,
		onTabChange: scrollToTop,
	})
	tabsetRef.current = primaryTabset
	root.appendChild(primaryTabset.root)

	return root
}
